import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';
import { uuidSchema } from './useLineItemPatch';

const nonBlankStringSchema = z.string().min(1).refine((value) => value.trim().length > 0, 'must not be blank');
export const auditOutcomeSchema = z.enum(['applied', 'noop', 'undone']);
export const auditTargetKindSchema = z.enum(['line_item', 'event', 'rule']);

export const auditUndoStateSchema = z.object({
  valid: z.boolean(),
  reason: z.string().nullable(),
}).strict().superRefine((undo, context) => {
  if (undo.valid && undo.reason !== null) context.addIssue({ code: 'custom', path: ['reason'], message: 'valid Undo cannot have a reason' });
  if (!undo.valid && (undo.reason === null || undo.reason.trim().length === 0)) context.addIssue({ code: 'custom', path: ['reason'], message: 'invalid Undo requires a reason' });
});
export type AuditUndoState = z.infer<typeof auditUndoStateSchema>;

export const transactionAuditEntrySchema = z.object({
  entry_id: uuidSchema,
  batch_id: uuidSchema,
  happened_at: z.string().datetime({ offset: true }),
  actor_label: nonBlankStringSchema,
  summary: nonBlankStringSchema,
  outcome: auditOutcomeSchema,
  target_kind: auditTargetKindSchema,
  affected_count: z.number().int().nonnegative(),
  undo: auditUndoStateSchema,
}).strict();
export type TransactionAuditEntry = z.infer<typeof transactionAuditEntrySchema>;

export const transactionAuditMetaSchema = z.object({ next_cursor: z.string().nullable() }).strict();
export const transactionAuditResponseSchema = z.object({
  data: z.array(transactionAuditEntrySchema),
  meta: transactionAuditMetaSchema,
}).strict();
export type TransactionAuditResponse = z.infer<typeof transactionAuditResponseSchema>;

export function parseTransactionAuditResponse(payload: unknown): { data: TransactionAuditEntry[]; nextCursor: string | null } {
  const parsed = parseEnvelope(z.array(transactionAuditEntrySchema), payload);
  const meta = transactionAuditMetaSchema.parse(parsed.meta ?? {});
  return { data: parsed.data, nextCursor: meta.next_cursor };
}

export function useTransactionAudit(canonicalTransactionId: string | undefined, cursor?: string) {
  return useQuery({
    queryKey: ['transactions', 'audit', canonicalTransactionId, { cursor: cursor ?? null, limit: 50 }],
    enabled: canonicalTransactionId !== undefined,
    retry: false,
    queryFn: async () => {
      const transactionId = uuidSchema.parse(canonicalTransactionId);
      const params = cursor === undefined ? { limit: 50 } : { cursor: z.string().min(1).parse(cursor), limit: 50 };
      const response = await client.get(`/api/transactions/${transactionId}/audit`, { params });
      return parseTransactionAuditResponse(response.data);
    },
  });
}
